import ApiService from "../Services/ApiService";

/**
 * Notifications in-app (cloche du tableau de bord), communes au prof et au client :
 * le backend sert toujours les notifications de l'utilisateur courant du token.
 */
export type NotificationCategory = "declaration" | "travel" | "billing" | "client" | "account";

export type NotificationPriority = "low" | "normal" | "high";

/** Destination logique, traduite en URL par src/config/NOTIFICATION_ROUTES.ts. */
export interface NotificationTarget {
    /** client_detail, client_list, documents, courses, settings, travel_subscription, notifications. */
    route: string;
    params: Record<string, unknown>;
}

export interface NotificationItem {
    id: number;
    type: string;
    /** Voir NotificationCategory ; reste une chaîne libre pour tolérer une catégorie ajoutée côté serveur. */
    category: string;
    title: string;
    body: string;
    data: Record<string, unknown>;
    target: NotificationTarget | null;
    priority: NotificationPriority;
    isRead: boolean;
    /** Date ISO 8601. */
    createdAt: string;
}

/** Filtres de la liste : ils ne portent que sur items et total, jamais sur les compteurs. */
export interface NotificationFilters {
    /** Catégorie unique, null pour toutes. */
    category: NotificationCategory | string | null;
    unreadOnly: boolean;
}

export interface NotificationCounts {
    /** Non lues (sous-titre du panneau). */
    unreadCount: number;
    /** Non lues arrivées depuis la dernière ouverture du panneau (badge de la cloche). */
    newCount: number;
}

export interface NotificationPage extends NotificationCounts {
    items: NotificationItem[];
    total: number;
}

export default abstract class NotificationRepository {
    /**
     * Notifications de l'utilisateur connecté, les plus récentes d'abord (masquées et
     * expirées exclues), éventuellement filtrées par catégorie et/ou non lues.
     */
    public static getNotifications(
        page: number = 1,
        limit: number = 20,
        filters?: Partial<NotificationFilters>,
    ): Promise<NotificationPage> {
        const params: Record<string, string | number> = { page, limit };
        if (filters?.category) params.category = filters.category;
        if (filters?.unreadOnly) params.unread = 1;
        return ApiService.get(`/v2/notifications`, params) as Promise<NotificationPage>;
    }

    /** Compteurs (non lues, nouvelles), interrogés par le polling de la cloche. */
    public static getUnreadCount(): Promise<NotificationCounts> {
        return ApiService.get(`/v2/notifications/unread-count`) as Promise<NotificationCounts>;
    }

    /** Le panneau vient d'être ouvert : le badge retombe à zéro, rien ne passe à lu. */
    public static markSeen(): Promise<{ newCount: number; seenAt: string | null }> {
        return ApiService.put(`/v2/notifications/seen`) as Promise<{ newCount: number; seenAt: string | null }>;
    }

    /** Marque une notification comme lue (404 si elle n'appartient pas à l'utilisateur connecté). */
    public static markAsRead(id: number): Promise<NotificationItem> {
        return ApiService.put(`/v2/notifications/${id}/read`) as Promise<NotificationItem>;
    }

    /** Marque toutes les notifications de l'utilisateur connecté comme lues. */
    public static markAllAsRead(): Promise<{ updated: number }> {
        return ApiService.put(`/v2/notifications/read-all`) as Promise<{ updated: number }>;
    }

    /** Masque une notification (jamais supprimée côté serveur ; 404 si elle n'est pas à l'utilisateur). */
    public static hide(id: number): Promise<{ hidden: boolean }> {
        return ApiService.put(`/v2/notifications/${id}/hide`) as Promise<{ hidden: boolean }>;
    }
}
